/**
 * LOP 1 / 3 - RASTER.
 *
 * Quy tac cung: pixel KHONG BAO GIO di qua bo dieu hoa cua React.
 *
 * React rat gioi trong viec dong bo mot cay DOM voi trang thai, va rat te trong
 * viec dieu phoi mot cong viec ve ton kem, khong dong bo, huy duoc, chay o toc
 * do khung hinh. Mot lan render trang la ca hai thu sau: mot tac vu bat dong bo
 * co the huy, va mot lan ghi 15 MB. Neu de React quan ly, ta se render lai trang
 * moi lan mot state khong lien quan thay doi.
 *
 * Vi the: React chi dung de dat canvas vao DOM voi kich thuoc dung. Toan bo
 * viec ve chay trong effect, qua requestAnimationFrame, va tu huy chinh no.
 */
import { useEffect, useRef } from 'react';
import type { PDFPageProxy, PdfRenderTask } from '../core/pdfjs';
import { isCancelled } from '../core/pdfjs';
import { acquireCanvas, releaseCanvas, clampRenderScale } from './canvasPool';

export interface CanvasLayerProps {
  page: PDFPageProxy | null;
  /** Kich thuoc hien thi tren man hinh, don vi CSS pixel. */
  cssWidth: number;
  cssHeight: number;
  /** Ti le zoom cua khung xem (1 = 72 DPI). */
  zoom: number;
  /**
   * true khi nguoi dung dang cuon nhanh: ve ban do phan giai thap cho re,
   * roi nang cap khi cuon dung lai. Mat ban cuon 60fps la loi te hon nhieu
   * so voi mot khoanh khac trang hoi mo.
   */
  draft: boolean;
  /**
   * DO XOAY HIEN THI - da la TONG cua /Rotate trong trang va phan xoay them cua
   * so do trang. Xem viewer/planView.ts.
   *
   * Dua THANG cho pdf.js chu khong xoay canvas bang CSS. Xoay bang CSS thi anh
   * dung nhung o chua van giu kich thuoc cu, va mot trang doc xoay thanh ngang
   * se bi cat cut hai dau. Day cung la cach anh thu nho o thanh ben lam, nen hai
   * khung hinh khong the lech nhau.
   */
  rotation: 0 | 90 | 180 | 270;
  /**
   * Bo dem phien ban cua lich su chinh sua.
   *
   * Canvas ve TAI LIEU GOC, nen voi loi trinh duyet hien nay mot lan sua khong
   * lam doi pixel - lop chinh sua (EditedTextLayer) moi la thu hien thay doi.
   * Nhung chu ky ve VAN phai chua so nay: khi loi Rust/PDFium den, no vat chat
   * hoa lenh ngay tren doi tuong trang, va luc do mot chu ky khong doi se giu
   * nguyen pixel CU tren man hinh ma khong co gi bao loi. Tra truoc mot lan ve
   * cho moi lan sua re hon nhieu so voi mot trang hien sai ma khong ai biet.
   */
  revision?: number;
  onRendered?: (pageIndex: number) => void;
}

/**
 * Do phan giai khi ve BAN NHAP cho mot trang LAN DAU lot vao khung trong luc cuon
 * (co dinh, nhe de cuon muot). Chu diem chinh chong lag / mo KHONG phai con so nay
 * ma la: KHONG ve lai cac trang DA CO san moi lan cuon - xem cua chan trong effect.
 * Da thu 0.6-theo-DPR truoc do va no lam render nang gap 4-16 lan -> lag khi cuon.
 */
const DRAFT_SCALE = 0.4;

/**
 * Len lich mot lan ve, tra ve ham huy.
 *
 * KHONG duoc dung thang requestAnimationFrame. rAF chi chay khi trang DANG
 * DUOC VE; trong tab an, tab nen, hoac khung nhin khong duoc composite, no
 * khong bao gio chay. Truoc day lan ve dau tien bi gan vao rAF, nen mot tai
 * lieu mo trong tab nen khong bao gio co pixel: khong thumbnail, va moi doan
 * ma cho onRendered deu treo vinh vien.
 *
 * Vi the: dung rAF de gop khung khi trang hien, va lui ve microtask khi an.
 * Trinh xem chinh thuc cua pdf.js cung khong gan lan ve dau vao rAF.
 */
function scheduleRender(run: () => void): () => void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    const t = setTimeout(run, 0);
    return () => clearTimeout(t);
  }
  const id = requestAnimationFrame(run);
  return () => cancelAnimationFrame(id);
}

export function CanvasLayer({
  page,
  cssWidth,
  cssHeight,
  zoom,
  draft,
  rotation,
  revision = 0,
  onRendered,
}: CanvasLayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // NOI DUNG dang nam tren canvas (trang, zoom, xoay, kich thuoc, ban sua) - KHONG
  // gom do phan giai. Va: ban ve do da o do phan giai DAY chua. Hai thu nay de
  // quyet dinh "co ve lai khong" ma khong ha cap mot trang da net khi bat dau cuon.
  const renderedContentRef = useRef<string>('');
  const renderedFullRef = useRef<boolean>(false);

  // Muon / tra canvas theo vong doi cua component.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const canvas = acquireCanvas();
    canvas.className = 'pageCanvas';
    host.appendChild(canvas);
    canvasRef.current = canvas;
    return () => {
      canvasRef.current = null;
      renderedContentRef.current = '';
      renderedFullRef.current = false;
      releaseCanvas(canvas);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !page || cssWidth <= 0 || cssHeight <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    const fullDpr = Math.min(dpr, 2);

    // CHU KY NOI DUNG - do xoay / zoom / kich thuoc / ban sua. KHONG gom do phan
    // giai. Do xoay PHAI nam trong day: thieu no thi bam "xoay phai" khong ve lai
    // gi ca (moi tham so khac y nguyen o mot trang vuong).
    const contentKey = `${page.pageNumber}:${zoom.toFixed(3)}:${Math.round(cssWidth)}:d${rotation}:r${revision}`;

    // KHONG HA CAP TRANG DA CO. Truoc day cu bat dau cuon la MOI trang dang hien
    // deu bi ve lai o do phan giai nhap (dang net bong mo di), roi khi dung lai ve
    // lai o do phan giai day - vua ton (nhieu lan ve moi lan cuon -> lag), vua nhay
    // mat. Gio:
    //   - dang cuon: chi ve trang CHUA CO gi cho noi dung nay (lan dau lot vao
    //     khung). Trang da co san - net hay nhap - deu GIU NGUYEN.
    //   - dung lai: nang cap trang con o do phan giai nhap len do phan giai day.
    // Vi tri khong nam trong chu ky nen keo trang qua lai khi cuon khong ve lai gi.
    let targetDpr: number;
    if (draft) {
      if (renderedContentRef.current === contentKey) return;
      targetDpr = DRAFT_SCALE;
    } else {
      if (renderedContentRef.current === contentKey && renderedFullRef.current) return;
      targetDpr = fullDpr;
    }
    const willBeFull = !draft;

    let cancelled = false;
    let task: PdfRenderTask | null = null;

    const unschedule = scheduleRender(() => {
      if (cancelled) return;

      const outputScale = clampRenderScale(cssWidth, cssHeight, targetDpr);
      const viewport = page.getViewport({ scale: zoom * outputScale, rotation });

      const pixelWidth = Math.max(1, Math.floor(viewport.width));
      const pixelHeight = Math.max(1, Math.floor(viewport.height));

      // Chi gan lai width/height khi that su doi: moi lan gan deu xoa sach
      // canvas va cap phat lai bo dem.
      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;

      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pixelWidth, pixelHeight);

      task = page.render({ canvasContext: ctx, viewport });
      task.promise
        .then(() => {
          if (cancelled) return;
          renderedContentRef.current = contentKey;
          renderedFullRef.current = willBeFull;
          onRendered?.(page.pageNumber - 1);
        })
        .catch((err: unknown) => {
          // Huy la duong di binh thuong: nguoi dung cuon qua trang khac.
          if (!isCancelled(err) && !cancelled) {
            console.error('[canvas] render loi', err);
          }
        });
    });

    return () => {
      cancelled = true;
      unschedule();
      // Huy tac vu dang chay. Bo qua buoc nay se de pdf.js ve len mot canvas
      // da bi tra ve be chua - hien tuong "trang ma" xuat hien sai cho.
      if (task) {
        try {
          task.cancel();
        } catch {
          // pdf.js nem loi neu tac vu da xong; vo hai.
        }
      }
    };
  }, [page, cssWidth, cssHeight, zoom, draft, rotation, revision, onRendered]);

  return <div className="canvasLayer" ref={hostRef} aria-hidden="true" />;
}
